// src/pages/Landing/LandingPage.jsx

import React, { useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';

// Ícones
import SchoolIcon from '@mui/icons-material/School';
import PaymentIcon from '@mui/icons-material/Payment';
import CampaignIcon from '@mui/icons-material/Campaign';
import HomeIcon from '@mui/icons-material/Home';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';

const LandingPage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // referência do formulário
  const formRef = useRef(null);

  const scrollToForm = () => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, p: 4 }}>
      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          mb: 6,
          py: 8,
          px: 4,
          background: 'linear-gradient(135deg, #1a73e8 30%, #4285f4 90%)',
          borderRadius: '16px',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Tooltip title="Voltar à página principal">
          <IconButton
            component={RouterLink}
            to="/"
            aria-label="Voltar à Página Principal"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: 'primary.light' },
            }}
          >
            <HomeIcon color="primary" />
          </IconButton>
        </Tooltip>

        <Typography variant={isSmallScreen ? 'h3' : 'h2'} gutterBottom sx={{ fontWeight: 'bold' }}>
          Zenkroll — Gestão Educacional com Integridade Académica
        </Typography>
        <Typography variant={isSmallScreen ? 'h6' : 'h5'} gutterBottom sx={{ mb: 4 }}>
          Pré-inscrições, pagamentos, campanhas e <strong>TCC</strong> com verificação de
          <strong> IA</strong> e <strong>Anti-Plágio</strong> — tudo num só lugar.
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={scrollToForm}
          sx={{
            borderRadius: '24px',
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)' },
          }}
        >
          Solicitar Demonstração Gratuita
        </Button>
      </Box>

      {/* BENEFÍCIOS */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4, color: 'primary.main' }}
        >
          Por que escolher o Zenkroll?
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              title: 'Gestão de TCC & Monografias',
              description:
                'Fluxo completo: proposta → projecto → monografia → defesa, com painéis e prazos.',
              icon: <LibraryBooksIcon fontSize="large" color="primary" />,
            },
            {
              title: 'Detecção de Escrita por IA',
              description:
                'Sinais de IA com score, evidências e relatórios explicáveis para decisão académica.',
              icon: <PsychologyIcon fontSize="large" color="secondary" />,
            },
            {
              title: 'Anti-Plágio',
              description:
                'Verificação de similaridade textual e trilha de auditoria para conformidade.',
              icon: <PlagiarismIcon fontSize="large" color="error" />,
            },
            {
              title: 'Pagamentos Inteligentes',
              description:
                'Integração com carteiras e bancos, recibos automáticos e redução de inadimplência.',
              icon: <PaymentIcon fontSize="large" color="success" />,
            },
            {
              title: 'Campanhas Eficazes',
              description:
                'Segmentação, captação e comunicação multicanal para aumentar adesão.',
              icon: <CampaignIcon fontSize="large" color="info" />,
            },
            {
              title: 'Relatórios Poderosos',
              description:
                'KPIs, análises e exportações (PDF/Excel/Word) para decisões rápidas.',
              icon: <AssessmentIcon fontSize="large" color="warning" />,
            },
          ].map((benefit, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: '16px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* SECÇÃO TCC, IA & ANTI-PLÁGIO */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4, color: 'primary.main' }}
        >
          TCC, IA & Anti-Plágio — Integridade académica de ponta a ponta
        </Typography>

        <Grid container spacing={4}>
          {/* TCC */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LibraryBooksIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Gestão de TCC & Monografias
                  </Typography>
                </Box>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {[
                    'Fluxo completo com estados e prazos',
                    'KPIs, estatísticas e agenda de defesas',
                    'Produtividade por supervisor e por departamento',
                    'Exportações: PDF, Excel e Word',
                  ].map((item) => (
                    <Typography key={item} variant="body2">
                      <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                      {item}
                    </Typography>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  component={RouterLink}
                  to="/dashboard-tcc"
                >
                  Explorar módulo TCC
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* IA */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PsychologyIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Sinais de IA explicáveis
                  </Typography>
                </Box>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {[
                    'Score de IA por documento',
                    'Evidências e explicações transparentes',
                    'Classificação de risco (baixo → crítico)',
                    'Histórico e auditoria de verificações',
                  ].map((item) => (
                    <Typography key={item} variant="body2">
                      <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                      {item}
                    </Typography>
                  ))}
                </Stack>
                <Button fullWidth variant="outlined" component={RouterLink} to="/tcc">
                  Ver políticas de integridade
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* ANTI-PLÁGIO */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PlagiarismIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Anti-Plágio & Similaridade
                  </Typography>
                </Box>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {[
                    'Cálculo de similaridade textual',
                    'Relatórios por secção e percentagens',
                    'Painel unificado de risco (IA, plágio, nota)',
                    'Trilha de auditoria e conformidade',
                  ].map((item) => (
                    <Typography key={item} variant="body2">
                      <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                      {item}
                    </Typography>
                  ))}
                </Stack>
                <Button fullWidth variant="outlined" component={RouterLink} to="/relatorios">
                  Ver relatórios
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* FORMULÁRIO (LEADS) */}
      <Box
        ref={formRef}
        sx={{
          mb: 6,
          p: 6,
          border: '1px solid #e0e0e0',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
          Solicite uma Demonstração Gratuita!
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 4 }}>
          Preencha o formulário para receber mais informações sobre o Zenkroll.
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={8} md={6}>
            <Box component="form" noValidate autoComplete="off">
              <TextField label="Nome" fullWidth required variant="outlined" sx={{ mb: 2 }} />
              <TextField label="Email" type="email" fullWidth required variant="outlined" sx={{ mb: 2 }} />
              <TextField label="Telefone" type="tel" fullWidth required variant="outlined" sx={{ mb: 2 }} />
              <TextField label="Instituição" fullWidth required variant="outlined" sx={{ mb: 4 }} />
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => alert('Formulário enviado com sucesso!')}
                sx={{
                  borderRadius: '24px',
                  fontWeight: 'bold',
                  py: 1.5,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)' },
                }}
              >
                Enviar Solicitação
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* FAQ */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4, color: 'primary.main' }}
        >
          Perguntas Frequentes
        </Typography>

        {[
          {
            question: 'O que é o Zenkroll?',
            answer:
              'O Zenkroll é uma plataforma completa de gestão educacional para o contexto moçambicano. Além de pré-inscrições, pagamentos e campanhas, inclui módulos de TCC, verificação de IA e Anti-Plágio, garantindo integridade académica.',
          },
          {
            question: 'Como funciona o módulo de TCC & Monografias?',
            answer:
              'O módulo cobre todo o ciclo: proposta, projecto, monografia e defesa, com KPIs, agenda de defesas, produtividade por supervisor/departamento e exportações (PDF/Excel/Word).',
          },
          {
            question: 'Como a plataforma detecta escrita por IA?',
            answer:
              'Usamos sinais de linguagem e modelos estatísticos para atribuir um score de IA por documento. As evidências são apresentadas de forma explicável, com classificação de risco e trilha de auditoria.',
          },
          {
            question: 'O Anti-Plágio faz comparação de similaridade?',
            answer:
              'Sim. Indicamos percentagem de similaridade, secções afectadas e um painel de risco que combina IA, Plágio e notas para apoiar decisões informadas.',
          },
          {
            question: 'Como funciona o período de teste gratuito?',
            answer:
              'Oferecemos 30 dias de teste sem compromisso, com todas as funcionalidades activas e suporte dedicado durante o período.',
          },
          {
            question: 'O sistema integra pagamentos locais?',
            answer:
              'Sim. Integração com carteiras e bancos em Moçambique, emissão de recibos, gestão de mensalidades e relatórios financeiros.',
          },
        ].map((faq, index) => (
          <Accordion key={index} sx={{ mb: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#f1f1f1' },
              }}
            >
              <Typography variant="h6">{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* RODAPÉ */}
      <Box sx={{ textAlign: 'center', mt: 8, py: 4, bgcolor: 'grey.100', borderRadius: '16px' }}>
        <Typography variant="body2" color="text.secondary">
          © 2025 Zenkroll. Todos os direitos reservados.
        </Typography>

        {/* Redes Sociais */}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Tooltip title="Facebook">
            <IconButton component="a" href="https://www.facebook.com/seu-perfil" target="_blank" rel="noopener noreferrer" aria-label="Facebook" sx={{ color: '#3b5998', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}>
              <FacebookIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Instagram">
            <IconButton component="a" href="https://www.instagram.com/seu-perfil" target="_blank" rel="noopener noreferrer" aria-label="Instagram" sx={{ color: '#E1306C', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}>
              <InstagramIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="LinkedIn">
            <IconButton component="a" href="https://www.linkedin.com/in/seu-perfil" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" sx={{ color: '#0e76a8', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}>
              <LinkedInIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="YouTube">
            <IconButton component="a" href="https://www.youtube.com/seu-canal" target="_blank" rel="noopener noreferrer" aria-label="YouTube" sx={{ color: '#FF0000', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}>
              <YouTubeIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Login & Registo */}
        <Box sx={{ mt: 4 }}>
          <Button
            component={RouterLink}
            to="/login"
            sx={{ mr: 2, color: 'primary.main', borderColor: 'primary.main', textTransform: 'none', '&:hover': { bgcolor: 'primary.light' } }}
            variant="outlined"
          >
            Login
          </Button>
          <Button
            component={RouterLink}
            to="/registro"
            sx={{ color: 'white', backgroundColor: 'primary.main', textTransform: 'none', '&:hover': { backgroundColor: 'primary.dark' } }}
            variant="contained"
          >
            Registe-se
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default LandingPage;
